<template>
  <div class="entity-detail">
    <div class="container py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <router-link :to="`/${entityType}`" class="btn btn-outline-secondary mb-2">
            <i class="bi bi-arrow-left me-2"></i>Back to List
          </router-link>
          <h1 class="mb-0">{{ formattedEntityType }} Details</h1>
        </div>
        <button 
          class="btn btn-primary" 
          @click="isEditing = !isEditing"
          v-if="entity"
        >
          <i :class="`bi ${isEditing ? 'bi-x-circle' : 'bi-pencil'} me-2`"></i>
          {{ isEditing ? 'Cancel Edit' : 'Edit' }}
        </button>
      </div>
      
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading {{ entityType }} details...</p>
      </div>
      
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        {{ error }}
      </div>
      
      <div v-else-if="!entity" class="alert alert-warning" role="alert">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        {{ entityType }} not found.
      </div>
      
      <div v-else class="card shadow">
        <div class="card-body">
          <form v-if="isEditing" @submit.prevent="saveChanges">
            <div v-for="field in entityFields" :key="field.key" class="mb-3">
              <label :for="field.key" class="form-label">{{ field.label }}</label>
              <input 
                :type="field.type" 
                class="form-control" 
                :id="field.key" 
                v-model="editedEntity[field.key]" 
                :required="field.required"
              >
            </div>
            
            <div class="d-flex justify-content-end mt-4">
              <button type="button" class="btn btn-secondary me-2" @click="isEditing = false">Cancel</button>
              <button type="submit" class="btn btn-success" :disabled="saving">
                <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Save Changes
              </button>
            </div>
          </form>
          
          <div v-else>
            <div class="row mb-4" v-for="field in entityFields" :key="field.key">
              <div class="col-md-3 fw-bold">{{ field.label }}:</div>
              <div class="col-md-9">{{ entity[field.key] || 'N/A' }}</div>
            </div>
            
            <div class="row mb-4">
              <div class="col-md-3 fw-bold">ID:</div>
              <div class="col-md-9">{{ entity.id }}</div>
            </div>
            
            <div class="row mb-4" v-if="entity.createdAt">
              <div class="col-md-3 fw-bold">Created At:</div>
              <div class="col-md-9">{{ formatDate(entity.createdAt) }}</div>
            </div>
            
            <div class="row mb-4" v-if="entity.updatedAt">
              <div class="col-md-3 fw-bold">Last Updated:</div>
              <div class="col-md-9">{{ formatDate(entity.updatedAt) }}</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Related Entities Section (if applicable) -->
      <div v-if="entity && entity.relatedEntities && entity.relatedEntities.length > 0" class="card shadow mt-4">
        <div class="card-header bg-light">
          <h5 class="mb-0">Related Entities</h5>
        </div>
        <div class="card-body">
          <ul class="list-group list-group-flush">
            <li v-for="relatedEntity in entity.relatedEntities" :key="relatedEntity.id" class="list-group-item">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ relatedEntity.name }}</strong>
                  <p class="text-muted mb-0" v-if="relatedEntity.description">{{ relatedEntity.description }}</p>
                </div>
                <router-link :to="`/${relatedEntity.type}/${relatedEntity.id}`" class="btn btn-sm btn-outline-primary">
                  View
                </router-link>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'EntityDetailView',
  data() {
    return {
      isEditing: false,
      editedEntity: {},
      saving: false,
      // These would ideally come from API metadata or be configured per entity type
      entityFields: [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'description', label: 'Description', type: 'text', required: false }
      ]
    };
  },
  computed: {
    ...mapGetters(['getEntityById', 'isLoading', 'hasError', 'getError']),
    entityType() {
      return this.$route.params.entityType;
    },
    entityId() {
      return this.$route.params.id;
    },
    formattedEntityType() {
      return this.entityType.charAt(0).toUpperCase() + this.entityType.slice(1);
    },
    entity() {
      return this.getEntityById(this.entityType, this.entityId);
    },
    loading() {
      return this.isLoading;
    },
    error() {
      return this.getError;
    }
  },
  methods: {
    ...mapActions(['fetchEntity', 'updateEntity']),
    async loadEntity() {
      try {
        await this.fetchEntity({
          entityType: this.entityType,
          id: this.entityId
        });
        this.resetEditForm();
      } catch (error) {
        console.error('Failed to load entity details:', error);
      }
    },
    resetEditForm() {
      if (this.entity) {
        this.editedEntity = { ...this.entity };
      }
    },
    async saveChanges() {
      this.saving = true;
      try {
        await this.updateEntity({
          entityType: this.entityType,
          id: this.entityId,
          data: this.editedEntity
        });
        this.isEditing = false;
      } catch (error) {
        console.error('Failed to update entity:', error);
      } finally {
        this.saving = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }
  },
  created() {
    this.loadEntity();
  },
  watch: {
    entityId() {
      this.loadEntity();
    },
    isEditing(newVal) {
      if (newVal) {
        this.resetEditForm();
      }
    }
  }
};
</script>
